import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { CellarEvents, type CellarEvent, type CellarEventActivity } from '@/lib/api';
import { Badge, EmptyState, Pill, Skeleton, TableHead } from '@/design-system';
import { formatPriceNok, formatDate } from '@/lib/format';

const ACTIVITIES: CellarEventActivity[] = ['added', 'drunk', 'gifted', 'sold', 'adjusted', 'moved', 'received', 'counted'];

const ACTIVITY_TONE: Record<CellarEventActivity, 'gold' | 'ruby' | 'mist' | 'success' | 'danger'> = {
  added: 'success',
  drunk: 'mist',
  gifted: 'gold',
  sold: 'ruby',
  adjusted: 'mist',
  moved: 'mist',
  received: 'success',
  counted: 'gold',
};

const ROW_COLUMNS = '110px 100px minmax(160px,2fr) 80px 90px 90px 90px';

/**
 * Kjellerhistorikk — the full transaction log (pos2_sales.webp style: date,
 * activity badge, wine, ±bottles, remaining, buy/sell price). Reads
 * GET /api/v1/cellar-events, written best-effort by inventory/orders/stock-
 * count routes — see backend/src/services/cellar-events.ts.
 */
export function HistorikkPage() {
  const { t } = useTranslation();
  const [activity, setActivity] = useState<CellarEventActivity | undefined>(undefined);

  const eventsQ = useQuery({
    queryKey: ['cellar-events', activity],
    queryFn: () => CellarEvents.list({ activity, limit: 100 }),
  });

  const events = eventsQ.data?.events ?? [];

  return (
    <div className="grid gap-6">
      <header>
        <h1 className="ui-section-title text-3xl">{t('history.title')}</h1>
        <p className="text-sm text-mist">{t('history.subtitle')}</p>
      </header>

      <div className="flex flex-wrap gap-2">
        <Pill active={!activity} onClick={() => setActivity(undefined)}>
          {t('history.filters.all')}
        </Pill>
        {ACTIVITIES.map((a) => (
          <Pill key={a} active={activity === a} onClick={() => setActivity(a)}>
            {t(`history.activity.${a}`)}
          </Pill>
        ))}
      </div>

      {eventsQ.isLoading ? (
        <Skeleton height={400} rounded="md" />
      ) : events.length === 0 ? (
        <EmptyState icon="clock" title={t('history.empty')} />
      ) : (
        <div className="overflow-x-auto">
          <div style={{ minWidth: 760 }}>
            <TableHead
              gridTemplateColumns={ROW_COLUMNS}
              columns={[
                t('history.columns.date'),
                t('history.columns.activity'),
                t('history.columns.wine'),
                t('history.columns.bottles'),
                t('history.columns.remaining'),
                t('history.columns.buy'),
                t('history.columns.sell'),
              ]}
            />
            <div className="grid gap-1 py-1">
              {events.map((event) => (
                <HistoryRow key={event.id} event={event} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function HistoryRow({ event }: { event: CellarEvent }) {
  const { t } = useTranslation();
  return (
    <div style={{ gridTemplateColumns: ROW_COLUMNS }} className="ui-sortable-row items-center">
      <span className="text-sm text-mist">{formatDate(event.createdAt)}</span>
      <Badge tone={ACTIVITY_TONE[event.activity]}>{t(`history.activity.${event.activity}`)}</Badge>
      <span className="truncate font-serif text-sm text-parchment">{event.wineName}</span>
      <span className={event.quantityDelta < 0 ? 'text-sm text-danger' : 'text-sm text-parchment'}>
        {event.quantityDelta > 0 ? `+${event.quantityDelta}` : event.quantityDelta}
      </span>
      <span className="text-sm text-mist">{event.remainingAfter}</span>
      <span className="text-sm text-mist">{formatPriceNok(event.purchasePriceNok)}</span>
      <span className="text-sm text-mist">{formatPriceNok(event.sellingPriceNok)}</span>
    </div>
  );
}
